import { Photo, PhotoMetadata } from '../types/photo';
import { getCurrentUser } from './localDatabase';
import { isOfflineModeEnabled } from './offlineStorage';
import { v4 as uuidv4 } from 'uuid';

const LOCAL_PHOTOS_KEY = 'installtrack_photos';

/**
 * Uploads a photo
 */
export const uploadPhoto = async (
  file: File,
  buildingId: string,
  apartmentId: string,
  metadata: PhotoMetadata,
  onProgress?: (progress: number) => void
): Promise<Photo> => {
  try {
    const user = getCurrentUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Generate a unique file name
    const fileExt = file.name.split('.').pop();
    const fileName = `${buildingId}/${apartmentId}/${Date.now()}.${fileExt}`;

    // Create object URL for local storage
    const objectUrl = URL.createObjectURL(file);
    
    // Create thumbnail
    const thumbnail = await createThumbnail(file);
    const thumbnailUrl = URL.createObjectURL(thumbnail);

    // Create photo object
    const photo: Photo = {
      id: uuidv4(),
      apartmentId,
      buildingId,
      url: objectUrl,
      thumbnailUrl: thumbnailUrl,
      fileName,
      fileSize: file.size,
      mimeType: file.type,
      createdAt: new Date(),
      createdBy: user.id,
      location: metadata.location,
      tags: metadata.tags,
      notes: metadata.notes,
      description: metadata.description,
      oneDriveUrl: metadata.oneDriveUrl,
      gpsLocation: metadata.gpsLocation,
      timestamp: metadata.timestamp
    };

    // Save to local storage
    const localPhotos = getLocalPhotos();
    localPhotos.push(photo);
    saveLocalPhotos(localPhotos);

    // Simulate upload progress
    if (onProgress) {
      onProgress(100);
    }

    return photo;
  } catch (error) {
    console.error('Error uploading photo:', error);
    throw error;
  }
};

/**
 * Creates a thumbnail from an image file
 */
const createThumbnail = async (file: File): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      const MAX_WIDTH = 300;
      const MAX_HEIGHT = 300;
      let width = img.width;
      let height = img.height;
      
      if (width > height) {
        if (width > MAX_WIDTH) {
          height *= MAX_WIDTH / width;
          width = MAX_WIDTH;
        }
      } else {
        if (height > MAX_HEIGHT) {
          width *= MAX_HEIGHT / height;
          height = MAX_HEIGHT;
        }
      }
      
      canvas.width = width;
      canvas.height = height;
      
      ctx?.drawImage(img, 0, 0, width, height);
      
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create thumbnail'));
        }
      }, file.type, 0.7);
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };
    
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Gets photos for a specific apartment
 */
export const getApartmentPhotos = async (apartmentId: string): Promise<Photo[]> => {
  try {
    if (!apartmentId) {
      return [];
    }
    
    const localPhotos = getLocalPhotos();
    return localPhotos.filter(photo => photo.apartmentId === apartmentId);
  } catch (error) {
    console.error('Error getting apartment photos:', error);
    return [];
  }
};

/**
 * Gets photos for a specific building
 */
export const getBuildingPhotos = async (buildingId: string): Promise<Photo[]> => {
  try {
    if (!buildingId) {
      return [];
    }
    
    const localPhotos = getLocalPhotos();
    return localPhotos.filter(photo => photo.buildingId === buildingId);
  } catch (error) {
    console.error('Error getting building photos:', error);
    return [];
  }
};

/**
 * Deletes a photo
 */
export const deletePhoto = async (photoId: string): Promise<void> => {
  try {
    const localPhotos = getLocalPhotos();
    const photoIndex = localPhotos.findIndex(photo => photo.id === photoId);
    
    if (photoIndex === -1) {
      throw new Error('Photo not found');
    }
    
    // Revoke object URLs to prevent memory leaks
    const photo = localPhotos[photoIndex];
    URL.revokeObjectURL(photo.url);
    URL.revokeObjectURL(photo.thumbnailUrl);
    
    // Remove from local storage
    localPhotos.splice(photoIndex, 1);
    saveLocalPhotos(localPhotos);
  } catch (error) {
    console.error('Error deleting photo:', error);
    throw error;
  }
};

/**
 * Gets local photos from localStorage
 */
export const getLocalPhotos = (): Photo[] => {
  try {
    const photosJson = localStorage.getItem(LOCAL_PHOTOS_KEY);
    if (!photosJson) {
      return [];
    }
    
    return JSON.parse(photosJson, (key, value) => {
      if (key === 'createdAt' || key === 'timestamp') {
        return value ? new Date(value) : undefined;
      }
      return value;
    });
  } catch (error) {
    console.error('Error getting local photos:', error);
    return [];
  }
};

/**
 * Saves local photos to localStorage
 */
export const saveLocalPhotos = (photos: Photo[]): void => {
  try {
    localStorage.setItem(LOCAL_PHOTOS_KEY, JSON.stringify(photos));
  } catch (error) {
    console.error('Error saving local photos:', error);
  }
};